<div class="form-wrapper">

  @if (this.magicLinkRequested && !this.passwordStep) {
    <div class="screen" [@screenAnim]="screenDir">
      <button type="button" class="btn-back" (click)="this.backFromMagicLink()" aria-label="Back">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M10 12L6 8l4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Back
      </button>

      <p class="magic-link-hint">
        Check your inbox (and spam folder). The link is one-time and expires in 15 minutes.
      </p>

      @if (this.magicLinkCodeSecondsRemaining > 0) {
        <div class="magic-link-countdown" role="status" aria-live="polite">
          <div class="magic-link-countdown-ring-wrap">
            <svg class="magic-link-countdown-svg" viewBox="0 0 100 100" aria-hidden="true">
              <g transform="rotate(-90 50 50)">
                <circle
                  class="magic-link-countdown-track"
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke-width="4"
                  pathLength="100"
                />
                <circle
                  class="magic-link-countdown-progress"
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke-width="4"
                  pathLength="100"
                  [attr.stroke-dasharray]="100"
                  [attr.stroke-dashoffset]="100 - (100 * this.magicLinkCodeSecondsRemaining / this.magicLinkCodeCountdownTotalSeconds)"
                />
              </g>
            </svg>
            <div class="magic-link-countdown-center">
              <span class="magic-link-countdown-number">{{ this.magicLinkCodeSecondsRemaining }}</span>
            </div>
          </div>
          <p class="magic-link-countdown-caption">seconds before you can resend the login link</p>
        </div>
      }

      @if (!this.showMagicLinkCodeField) {
        <button type="button" class="btn-secondary" (click)="this.openCodeEntry()">
          Enter code from email
        </button>
      }

      @if (this.showMagicLinkCodeField) {
        <div class="field-group magic-link-code-group" [@codeEntry]>
          <div
            class="magic-link-code-boxes"
            role="group"
            aria-label="6-digit verification code from your email"
            [attr.aria-invalid]="this.magicLinkCodeVerifyError ? 'true' : null"
          >
            @for (idx of this.magicLinkCodeBoxIndexes; track idx) {
              <input
                #magicLinkCodeBox
                type="text"
                inputmode="numeric"
                maxlength="1"
                class="magic-link-code-box"
                [class.magic-link-code-box-invalid]="!!this.magicLinkCodeVerifyError"
                [attr.id]="idx === 0 ? 'magic-link-code-box-0' : null"
                [attr.autocomplete]="idx === 0 ? 'one-time-code' : 'off'"
                [attr.aria-label]="'Digit ' + (idx + 1) + ' of 6'"
                [attr.aria-invalid]="this.magicLinkCodeVerifyError ? 'true' : null"
                [formControl]="this.magicLinkCodeDigitControls[idx]"
                (input)="this.onMagicLinkDigitInput(idx, $event)"
                (keydown)="this.onMagicLinkDigitKeydown(idx, $event)"
                (paste)="this.onMagicLinkDigitPaste(idx, $event)"
              />
            }
          </div>
          @if (this.magicLinkCodeVerifyError) {
            <p class="magic-link-code-error" role="alert">{{ this.magicLinkCodeVerifyError }}</p>
          } @else if (this.magicLinkCodeBoxesTouched && !this.magicLinkCodeComplete) {
            <p class="magic-link-code-error" role="alert">
              Enter the <strong>6-digit</strong> code from your email
            </p>
          }
        </div>
      }

      <div class="magic-link-inbox-links" aria-label="Open your email">
        <a
          class="magic-link-inbox-link"
          href="https://mail.google.com/mail/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            class="magic-link-inbox-icon"
            src="assets/login/gmail.png"
            alt=""
            width="18"
            height="18"
            decoding="async"
          />
          Open Gmail
        </a>
        <a
          class="magic-link-inbox-link"
          href="https://outlook.live.com/mail/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            class="magic-link-inbox-icon"
            src="assets/login/logo.png"
            alt=""
            width="18"
            height="18"
            decoding="async"
          />
          Open Outlook
        </a>
      </div>

      <div class="magic-link-actions-row">
        @if (this.showMagicLinkCodeField) {
          <button
            class="btn-primary magic-link-verify-btn"
            type="button"
            (click)="this.verifyMagicLinkCode()"
            [disabled]="this.verifyingMagicLinkCode || !this.magicLinkCodeComplete"
          >
            @if (this.verifyingMagicLinkCode) {
              <span class="btn-spinner" aria-hidden="true"></span>
            }
            {{ this.verifyingMagicLinkCode ? 'Verifying…' : 'Verify Code' }}
          </button>
        }
        @if (!this.magicLinkResendBlocked) {
          <button
            type="button"
            class="magic-link-resend-link"
            (click)="this.requestMagicLink()"
            [disabled]="this.requestingMagicLink"
          >
            Resend login link
          </button>
        }
      </div>
    </div>
  } @else {
    <div class="screen" [@screenAnim]="screenDir">
      <div class="field-group">
        <label class="field-label" for="email">Email or username</label>
        <mat-form-field
          class="icteam-form-field"
          [class.email-not-found]="!!this.emailNotFoundMessage"
          appearance="outline"
          subscriptSizing="dynamic"
        >
          <input
            matInput
            id="email"
            type="text"
            [formControl]="this.userNameController"
            (input)="this.clearMagicLinkFieldErrors()"
            placeholder="you@example.com"
            autocomplete="email"
            [attr.aria-invalid]="this.emailNotFoundMessage ? 'true' : null"
            [attr.aria-describedby]="this.emailNotFoundMessage ? 'email-not-found-error' : null"
          />
          @if (this.userNameController.hasError('magicLinkEmail')) {
            <mat-error>Enter a valid <strong>email address</strong> to receive a login link</mat-error>
          }
          @if (this.userNameController.hasError('required') && !this.userNameController.hasError('magicLinkEmail') && !this.emailNotFoundMessage) {
            <mat-error>This field is <strong>required</strong></mat-error>
          }
        </mat-form-field>
        @if (this.emailNotFoundMessage) {
          <p
            id="email-not-found-error"
            class="magic-link-request-error"
            role="alert"
          >{{ this.emailNotFoundMessage }}</p>
        } @else if (this.magicLinkRequestError) {
          <div class="magic-link-request-error" role="alert">{{ this.magicLinkRequestError }}</div>
        }
      </div>

      @if (this.passwordStep) {
        <div class="field-group" [@passwordField]>
          <div class="field-label-row">
            <label class="field-label" for="password">Password</label>
            <a href="#" class="forgot-link" tabindex="-1">Forgot?</a>
          </div>
          <mat-form-field class="icteam-form-field" appearance="outline">
            <input
              matInput
              id="password"
              [type]="hidePassword ? 'password' : 'text'"
              [formControl]="this.passwordController"
              (keyup)="handleKeyUp($event)"
              placeholder="Enter your password"
              autocomplete="current-password"
            />
            <button mat-icon-button matSuffix type="button" (click)="togglePasswordVisibility()" [attr.aria-label]="'Toggle password visibility'" [attr.aria-pressed]="!hidePassword">
              <mat-icon>{{ hidePassword ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
            @if (this.passwordController.hasError('required')) {
              <mat-error>Password is <strong>required</strong></mat-error>
            }
          </mat-form-field>
        </div>

        <div class="sign-in-actions-col">
          <button class="btn-primary" type="button" (click)="this.login()" [disabled]="isLoading">
            @if (isLoading) {
              <span class="btn-spinner" aria-hidden="true"></span>
            }
            {{ isLoading ? 'Signing in…' : 'Log in' }}
          </button>
          <button
            class="btn-ghost"
            type="button"
            (click)="this.switchToMagicLinkFromPassword()"
            [disabled]="this.requestingMagicLink"
          >
            Use a login link instead
          </button>
        </div>
      } @else {
        <div class="sign-in-actions-col">
          <button
            class="btn-primary"
            type="button"
            (click)="this.requestMagicLink()"
            [disabled]="!this.hasIdentifier || this.requestingMagicLink"
          >
            @if (requestingMagicLink) {
              <span class="btn-spinner" aria-hidden="true"></span>
            }
            {{ requestingMagicLink ? 'Sending…' : 'Send me a login link' }}
          </button>
          <button
            class="btn-secondary"
            type="button"
            (click)="this.continueToPassword()"
            [disabled]="!this.hasIdentifier"
          >
            Continue with password
          </button>
        </div>
      }
    </div>
  }

</div>
